import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { initializeApp } from 'firebase/app';
import { firebaseConfig } from './firebase-config';
import { useAuth } from './AuthContext';
import { useLanguage } from './contexts/LanguageContext';

const app = initializeApp(firebaseConfig);
const functions = getFunctions(app);

export default function VerifyClaim() {
	const { token } = useParams();
	const navigate = useNavigate();
	const { login } = useAuth();

	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [message, setMessage] = useState('');
	const [loading, setLoading] = useState(false);
	const [tokenValid, setTokenValid] = useState(null);
	const [claimData, setClaimData] = useState(null);
	const { t } = useLanguage();

	useEffect(() => {
		// Verify token when component mounts
		const verifyToken = async () => {
			try {
				const validateClaimToken = httpsCallable(functions, 'validateClaimToken');
				const result = await validateClaimToken({ token });

				setTokenValid(true);
				setClaimData(result.data);
			} catch (error) {
				setTokenValid(false);
				let errorMessage = t('invalidOrExpiredLink');

				switch (error.code) {
					case 'not-found':
						errorMessage = t('linkNotFound');
						break;
					case 'deadline-exceeded':
						errorMessage = t('linkExpiredRequestNew');
						break;
					default:
						errorMessage = error.message;
				}

				setMessage(errorMessage);
			}
		};

		if (token) {
			verifyToken();
		}
	}, [token]);

	const handleVerifyClaim = async (e) => {
		e.preventDefault();
		setMessage('');

		if (password !== confirmPassword) {
			setMessage(t('passwordMismatch'));
			return;
		}

		if (password.length < 6) {
			setMessage(t('passwordTooShort'));
			return;
		}

		setLoading(true);

		try {
			const completeClaim = httpsCallable(functions, 'completeClaim');
			const result = await completeClaim({
				token,
				password,
			});

			// Auto-login the user
			await login(claimData.email, password);

			// Redirect to their person page
			navigate(`/${result.data.personId}`);
		} catch (error) {
			let errorMessage = t('claimErrorGeneric');

			switch (error.code) {
				case 'not-found':
					errorMessage = t('linkNotFound');
					break;
				case 'deadline-exceeded':
					errorMessage = t('linkExpiredRequestNew');
					break;
				case 'already-exists':
					errorMessage = t('accountAlreadyClaimed');
					break;
				case 'internal':
					errorMessage = t('failedToCreateAccount');
					break;
				default:
					errorMessage = error.message;
			}

			setMessage(errorMessage);
		}

		setLoading(false);
	};

	if (tokenValid === null) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<div className='text-center'>
					<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4'></div>
					<p className='text-gray-600'>{t('verifyingLink')}</p>
				</div>
			</div>
		);
	}

	if (tokenValid === false) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4'>
				<div className='max-w-md w-full bg-white rounded-lg shadow-lg p-6'>
					<div className='text-center'>
						<div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4'>
							<svg className='h-6 w-6 text-red-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth='2'
									d='M6 18L18 6M6 6l12 12'
								/>
							</svg>
						</div>
						<h2 className='text-2xl font-bold text-gray-900 mb-2'>{t('invalidLink')}</h2>
						<p className='text-red-600 mb-4'>{message}</p>
						<button
							onClick={() => navigate('/')}
							className='w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600'
						>
							{t('goToLogin')}
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4'>
			<div className='max-w-md w-full bg-white rounded-lg shadow-lg p-6'>
				<div className='text-center mb-6'>
					<div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4'>
						<svg className='h-6 w-6 text-green-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
							<path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M5 13l4 4L19 7' />
						</svg>
					</div>
					<h2 className='text-2xl font-bold text-gray-900 mb-2'>{t('completeAccountClaim')}</h2>
					<p className='text-gray-600 text-sm'>{t('emailVerifiedCreatePassword')}</p>
					{claimData && <p className='text-sm text-blue-600 mt-2'>Account: {claimData.email}</p>}
				</div>

				<form onSubmit={handleVerifyClaim} className='space-y-4'>
					<div>
						<label className='block text-sm font-medium text-gray-700 mb-1'>
							{t('createPasswordLabel')}
						</label>
						<input
							type='password'
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							className='w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
							placeholder={t('enterYourPassword')}
							required
							minLength={6}
						/>
					</div>

					<div>
						<label className='block text-sm font-medium text-gray-700 mb-1'>
							{t('confirmPasswordLabel')}
						</label>
						<input
							type='password'
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							className='w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
							placeholder={t('confirmYourPassword')}
							required
							minLength={6}
						/>
					</div>

					{message && (
						<div className='text-red-600 text-sm text-center bg-red-50 border border-red-200 rounded-md p-2'>
							{message}
						</div>
					)}

					<button
						type='submit'
						disabled={loading}
						className='w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
					>
						{loading ? t('creatingAccount') : t('completeClaim')}
					</button>
				</form>

				<div className='mt-4 text-center'>
					<button onClick={() => navigate('/')} className='text-blue-600 hover:text-blue-500 text-sm'>
						{t('backToLogin')}
					</button>
				</div>
			</div>
		</div>
	);
}
